import { useId } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { App } from "antd";
import { Plus, X } from "lucide-react";

import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseIconButton from "@/components_v2/Button/BaseIconButton";
import { BUTTON_VARIANTS } from "@/components_v2/Button/constants";
import { HookFormInput } from "@/components_v2/FormFields/ReactHookForm/fields";
import { useInviteParticipantsMutation } from "@/redux/service";
import { handleErrors } from "@/shared/utils/forms";
import {
  INVITE_MODAL_WIDTH,
  MAX_INVITE_EMAILS,
} from "@/features/certifications/invitations/constants";
import {
  InviteFormValues,
  InviteModalProps,
} from "@/features/certifications/invitations/types";

import "../styles.css";

const ICON_SIZE = 16;
const EMPTY_ROW = { email: "" };
const DEFAULT_VALUES: InviteFormValues = { emails: [EMPTY_ROW] };

const InviteModal = ({
  open,
  certification,
  onClose,
  onInvited,
}: InviteModalProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const labelId = useId();
  const descriptionId = useId();
  const [invite, { isLoading }] = useInviteParticipantsMutation();

  const schema: yup.ObjectSchema<InviteFormValues> = yup.object({
    emails: yup
      .array()
      .of(
        yup.object({
          email: yup
            .string()
            .trim()
            .email(t("certification.admin.inviteModal.invalidEmail"))
            .required(t("certification.admin.inviteModal.emailRequired")),
        }),
      )
      .max(
        MAX_INVITE_EMAILS,
        t("certification.admin.inviteModal.tooMany", {
          max: MAX_INVITE_EMAILS,
        }),
      )
      .test("unique", "", (rows, context) => {
        const emails = (rows ?? []).map(({ email }) =>
          email.trim().toLowerCase(),
        );
        const index = emails.findIndex(
          (email, position) => emails.indexOf(email) !== position,
        );
        return index === -1
          ? true
          : context.createError({
              path: `emails.${index}.email`,
              message: t("certification.admin.inviteModal.duplicate"),
            });
      })
      .required(),
  });

  const form = useForm<InviteFormValues>({
    resolver: yupResolver(schema),
    defaultValues: DEFAULT_VALUES,
  });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "emails",
  });
  const emails = useWatch({ control: form.control, name: "emails" });
  const canAddRow = Boolean(emails.at(-1)?.email.trim());

  const handleClose = () => {
    form.reset(DEFAULT_VALUES);
    onClose();
  };

  const handleSubmit = async ({ emails }: InviteFormValues) => {
    const requested = emails.map(({ email }) => email.trim());
    try {
      const created = await invite({
        certificationSlug: certification.slug,
        emails: requested,
      }).unwrap();
      const skipped = requested.length - created.length;
      notification.success({
        message: t("certification.admin.inviteModal.sent", {
          count: created.length,
        }),
        description:
          skipped > 0
            ? t("certification.admin.inviteModal.skipped", { count: skipped })
            : undefined,
      });
      form.reset(DEFAULT_VALUES);
      onInvited();
    } catch (errors) {
      handleErrors(
        errors as Record<string, unknown>,
        form.setError,
        notification,
      );
    }
  };

  return (
    <BaseModal
      open={open}
      width={INVITE_MODAL_WIDTH}
      onCancel={handleClose}
      title={t("certification.admin.inviteModal.title", {
        title: certification.title,
      })}
      footer={
        <div className="flex justify-end gap-200">
          <BaseButton
            variant={BUTTON_VARIANTS.TERTIARY}
            disabled={isLoading}
            onClick={handleClose}
          >
            {t("buttons.cancel")}
          </BaseButton>

          <BaseButton
            loading={isLoading}
            onClick={form.handleSubmit(handleSubmit)}
          >
            {t("certification.admin.inviteModal.submit")}
          </BaseButton>
        </div>
      }
    >
      <div className="flex flex-col gap-300">
        <div className="flex flex-col gap-50">
          <h6 id={labelId} className="body-s-medium">
            {t("certification.admin.inviteModal.label")}
          </h6>

          <p
            id={descriptionId}
            className="invite-modal-description body-s-regular"
          >
            {t("certification.admin.inviteModal.description")}
          </p>
        </div>

        <ul
          className="invite-modal-list flex flex-col gap-200"
          aria-labelledby={labelId}
          aria-describedby={descriptionId}
        >
          {fields.map((field, index) => (
            <li key={field.id} className="flex items-start gap-100">
              <div className="flex-1">
                <HookFormInput
                  name={`emails.${index}.email`}
                  control={form.control}
                  type="text"
                  inputMode="email"
                  autoComplete="off"
                  placeholder={t("certification.admin.inviteModal.placeholder")}
                  aria-label={t("certification.admin.inviteModal.emailLabel", {
                    number: index + 1,
                  })}
                />
              </div>

              {fields.length > 1 && (
                <BaseIconButton
                  icon={<X size={ICON_SIZE} />}
                  variant={BUTTON_VARIANTS.LINK}
                  ariaLabel={t("certification.admin.inviteModal.remove", {
                    number: index + 1,
                  })}
                  onClick={() => remove(index)}
                />
              )}
            </li>
          ))}
        </ul>

        {fields.length < MAX_INVITE_EMAILS && (
          <div>
            <BaseButton
              variant={BUTTON_VARIANTS.LINK}
              leadingIcon={<Plus size={ICON_SIZE} />}
              disabled={!canAddRow}
              onClick={() => append(EMPTY_ROW)}
            >
              {t("certification.admin.inviteModal.addUser")}
            </BaseButton>
          </div>
        )}
      </div>
    </BaseModal>
  );
};

export default InviteModal;
